export interface MethodologySection {
  id: string;
  title: string;
  body: string[];
  details: string[];
}

export interface RiskLevelDefinition {
  level: 'high' | 'medium' | 'low' | 'unknown';
  label: string;
  description: string;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string[];
  category: 'basics' | 'process' | 'limits' | 'expert';
  priority: 'top6' | 'extended';
}

export const methodologyHero = {
  eyebrow: 'Legislativní monitor',
  title: 'Hlídáme zákony, které mohou oslabit demokracii',
  lead: 'Každý den systém sleduje nové sněmovní tisky a hledá návrhy, které se mohou dotýkat právního státu, demokratických institucí, občanských práv nebo kontroly veřejné moci.',
  honestNote: 'Nejde o právní stanovisko ani automatický verdikt. Monitor upozorňuje na rizikové signály, vysvětluje důvody zařazení a pomáhá rychle najít návrhy, které si zaslouží lidskou kontrolu.',
};

// @deprecated — používán starým AboutMethodologyPage layoutem; nové bloky jsou v pipelineSteps, tabsGuide atd.
export const methodologySections: MethodologySection[] = [
  {
    id: 'capture',
    title: '1. Zachycení návrhu',
    body: [
      'Systém sleduje dostupné veřejné zdroje Poslanecké sněmovny. Když se objeví nový návrh nebo aktualizace, uloží základní údaje: číslo tisku, název, odkaz na detail, datum a krátký popis.',
      'V této fázi se ještě nerozhoduje, jestli je návrh problematický. Cílem je hlavně nic nepřehlédnout a dostat položku do přehledu.',
    ],
    details: [
      'Tahle část je hlavně sběr dat. Systém se dívá, co je nového, a připraví položku tak, aby ji bylo možné dál zobrazit nebo analyzovat.',
      'AI zde není hlavní rozhodovací prvek. Důležitější je spolehlivě zachytit návrh a odkázat na původní zdroj.',
    ],
  },
  {
    id: 'triage',
    title: '2. Rychlé roztřídění',
    body: [
      'První filtr oddělí běžnou agendu od návrhů, které vypadají citlivěji. Kritické oblasti jsou zejména volby a referendum, ústavní pořádek, politické strany, justice a trestní proces, policie, média, svoboda projevu, soukromí, shromažďování, sdružování, diskriminace a menšiny.',
      'Nový návrh v kritické oblasti může jít do AI už z prvního zveřejněného textu před prvním čtením. Rutinní nebo sektorové návrhy systém dál sleduje, ale neposílá je do AI jen proto, že se objevily.',
    ],
    details: [
      'Tady se používají jednoduché signály a pravidla: oblast návrhu, název, dostupné shrnutí, typ dokumentu a další indicie.',
      'Smyslem není udělat konečný závěr. Smyslem je rozhodnout, jestli má cenu zapojit další AI analýzu. Příklady: volební novela nebo trestní proces ano, běžná daňová nebo sektorová novela obvykle až při silnější události.',
    ],
  },
  {
    id: 'first-pass',
    title: '3. První kolo AI analýzy',
    body: [
      'První AI analýza se dívá na návrh široce. Ptá se: je tady něco, co může mít dopad na práva, demokracii, kontrolu moci nebo fungování institucí?',
      'Výstupem je předběžná míra rizika, krátké vysvětlení a seznam důvodů. Tato fáze má být spíš citlivá než přísná: raději upozorní na možný problém, než aby ho předčasně zahodila.',
    ],
    details: [
      'AI v prvním kole funguje jako rychlý analytický skener. Hledá slova, mechanismy a souvislosti, které by mohly znamenat citlivý dopad.',
      'Výsledek prvního kola se nebere jako definitivní. Slouží jako podklad pro další kontrolu.',
    ],
  },
  {
    id: 'second-pass',
    title: '4. Druhé kolo AI analýzy',
    body: [
      'Druhé čtení bere výsledek prvního kola a kontroluje ho přísněji. Neptá se jen „je tam signál?", ale také „je ten signál opravdu důležitý a čím je podložený?"',
      'Tady se zpřesňuje skóre, důvody i oblasti dopadu. Pokud první kolo přestřelilo, druhé ho může stáhnout. Pokud naopak najde silnější mechanismus rizika, může návrh posunout výš.',
    ],
    details: [
      'Druhé kolo navazuje na první: bere jeho důvody a zkouší je zpevnit nebo zpochybnit.',
      'AI se víc soustředí na to, jestli riziko opravdu plyne z návrhu, nebo jen z obecného dojmu. Proto se může výsledek mezi prvním a druhým kolem změnit.',
    ],
  },
  {
    id: 'third-pass',
    title: '5. Třetí kolo AI analýzy',
    body: [
      'Třetí kolo používá pokročilý „přemýšlivý“ AI model s větší schopností uvažování a pracuje ve třech krocích. Nejprve přečte návrh zcela nezávisle — bez znalosti předchozích kol. Teprve potom se podívá na výsledky 1. a 2. kola a každý závěr buď potvrdí, zpřesní, nebo zamítne.',
      'Výstupem má být finální shrnutí: jestli návrh vyžaduje pozornost, jak vysoké je riziko, proč, v jakých oblastech a s jakou jistotou. Pokud tato část chybí nebo selže, dashboard výsledek neprezentuje jako hotovou analýzu.',
    ],
    details: [
      'Klíčová vlastnost třetího kola: může závěry druhého kola odmítnout. Pokud nezávislé čtení ukáže, že riziko neexistuje nebo bylo přeceněno, model to řekne explicitně — a výsledek se liší od Stage 2.',
      'Každé dotčené ustanovení dostane přesnou citaci textu zákona, popis demokratického problému a typ důkazu (přímý text / institucionální inference / hypotetické zneužití). Právě proto je třetí kolo nejdůležitější pro závěr, ale pořád nejde o právně závazné rozhodnutí.',
    ],
  },
  {
    id: 'published-result',
    title: '6. Co se zobrazí v dashboardu',
    body: [
      'Záložka Analýzy ukazuje základní přehled všech zachycených návrhů. Je vhodná pro rychlou orientaci: co přibylo, jaký je název, oblast a zda už existuje nějaké posouzení.',
      'Záložka Analýzy ukazuje publikovanou podobu hlubší analýzy. U každé položky je důležité sledovat stav: dokončeno, rozpracováno, problém, nebo neposouzeno.',
    ],
    details: [
      'Pokud je výsledek rozpracovaný, chybový nebo označený jako neposouzený, nečtěte ho jako závěr. Znamená to, že některá část řetězce ještě nedoběhla nebo nebyla dost spolehlivá.',
      'Dokončený výsledek má shrnutí, míru rizika, důvody a zpravidla i vysvětlení, proč má návrh dostat danou prioritu.',
    ],
  },
  {
    id: 'tracking-range',
    title: '7. Kam má sledování směřovat',
    body: [
      'Cílový směr je sledovat návrh od podání v PSP ČR až po schválení, zamítnutí nebo ukončení projednávání. To zahrnuje i změny v průběhu projednávání, pokud budou dostupné v datech.',
      'Dashboard ale netvrdí, že už dnes umí celý legislativní život návrhu zachytit dokonale. Ukazuje to, co prošlo aktuálně dostupnou sběrnou, analytickou a publikační vrstvou.',
    ],
    details: [
      'Do budoucna je důležité sledovat i to, jestli se návrh během projednávání změní. Pozměňovací návrh může někdy změnit význam celého tisku.',
      'Aktuální stránka proto mluví opatrně: ukazuje dostupný stav, ne slib úplného pokrytí každého kroku legislativního procesu.',
    ],
  },
  {
    id: 'limits',
    title: '8. Co výsledek neznamená',
    body: [
      'Vysoké riziko neznamená, že je návrh špatný. Znamená, že by ho měl člověk číst přednostně a pečlivě.',
      'Nízké riziko neznamená, že je návrh bezvýznamný. Znamená jen, že v dostupných datech systém nenašel silný varovný signál.',
      'AI se může mýlit. Proto je výstup pomůcka pro kontrolu, ne náhrada právního nebo politického úsudku.',
    ],
    details: [
      'AI nemá vlastní odpovědnost a nezná celý politický ani právní kontext. Umí pomoci najít podezřelé mechanismy, ale neumí nahradit odbornou debatu.',
      'Nejlepší použití dashboardu je proto praktické: rychle poznat, které návrhy stojí za detailní lidské čtení.',
    ],
  },
];

export const riskLevels: RiskLevelDefinition[] = [
  {
    level: 'high',
    label: 'VYSOKÉ',
    description:
      'Číst přednostně. Systém našel silnější signály možného zásahu do práv, kontroly moci nebo fungování institucí.',
  },
  {
    level: 'medium',
    label: 'STŘEDNÍ',
    description:
      'Stojí za pozornost. Návrh se dotýká citlivější oblasti, ale dostupné důvody zatím nejsou nejsilnější.',
  },
  {
    level: 'low',
    label: 'NÍZKÉ',
    description:
      'Bez výrazného varovného signálu v dostupných datech. Pořád může jít o důležitý návrh.',
  },
  {
    level: 'unknown',
    label: 'NEPOSOUZENO',
    description:
      'Není hotová analýza nebo chybí finální rozhodnutí. Taková položka není verdikt.',
  },
];

// ─── Pipeline ─────────────────────────────────────────────────────────────────

export interface PipelineStep {
  number: 1 | 2 | 3 | 4 | 5;
  title: string;
  oneliner: string;
  detail: string;
  cost: string | null;
  techNote: string | null;
  color: 'blue' | 'gray' | 'purple' | 'coral' | 'teal';
}

export const pipelineSteps: PipelineStep[] = [
  { number: 1, title: 'Zachycení návrhu', oneliner: 'Nový sněmovní tisk se objeví v systému.', detail: 'Systém zachytí nový nebo změněný návrh a uloží základní údaje, aby se dal dál sledovat v dashboardu.', cost: null, techNote: 'PSP RSS / zdroj PSP', color: 'blue' },
  { number: 2, title: 'První filtr', oneliner: 'Do AI nejdou všechny nové návrhy automaticky.', detail: 'Pravidla zvýhodní kritické oblasti: volby, ústavní pořádek, politické strany, justici, trestní proces, policii, média a základní práva. Takové návrhy mohou jít do AI už před prvním čtením; rutinní agenda zůstává jen ve sledování, dokud nepřijde silnější událost.', cost: null, techNote: 'pravidla a ranking bez AI', color: 'gray' },
  { number: 3, title: 'První AI čtení', oneliner: 'Model hledá možné zásahy do práv, institucí nebo kontroly moci.', detail: 'První čtení je široké a citlivé. Má najít signály, které by mohly stát za další kontrolu.', cost: null, techNote: 'lokální model', color: 'purple' },
  { number: 4, title: 'Kontrolní AI čtení', oneliner: 'Druhé kolo ověřuje, jestli jsou důvody dostatečně silné.', detail: 'Kontrolní čtení zpřesňuje skóre a důvody. Slabší poplachy může stáhnout, silnější naopak potvrdit.', cost: null, techNote: 'kontrolní model', color: 'purple' },
  { number: 5, title: 'Finální rozhodnutí', oneliner: 'Třetí AI model je pokročilejší „přemýšlivá“ kontrola.', detail: 'Má větší schopnost uvažování než předchozí kola. Nepřebírá jejich závěry: projde text sám a teprve potom porovná, co první dvě kola říkala — a může jejich verdikt odmítnout. Výsledkem je finální hodnocení rizika a případný Telegram alert.', cost: null, techNote: 'reasoning model / finální kontrola', color: 'coral' },
];

export const pipelineMiniFaqs = [
  { q: 'Co projde dál?', a: 'Přednost mají kritické oblasti a důležité legislativní události. Nový volební nebo justiční návrh může jít do AI hned; běžná sektorová novela obvykle zůstane jen ve sledování.' },
  { q: 'Kdy přijde Telegram alert?', a: 'Jen pokud finální kolo potvrdí vyšší riziko a najde srozumitelný scénář zneužití. Cíl je několik důležitých alertů měsíčně, ne denní spam.' },
];

export const pipelineExpertDetail = `Datový tok je popsaný v docs/PROPOSAL_LIFECYCLE_PUBLISH_CONTRACT.md a docs/PROPOSAL_LIFECYCLE_BRIDGE_CONTRACT.md. Klíčové runtime soubory: runtime/items.json (ingest output) → runtime/proposal-tracking-state.json + proposal-events.jsonl (lifecycle vrstva) → runtime/proposal-ranking-queue.json (po rankingu) → runtime/watchdog-queue.json (enqueue) → runtime/watchdog-queue-results.json (po Stage 3) → runtime reconcile zpět do tracking-state → publish-proposal-lifecycle.js generuje public/data/proposal-lifecycle.published.json. Frontend čte výhradně published JSON, runtime soubory jsou interní.`;

// ─── Tabs guide ───────────────────────────────────────────────────────────────

export const riderMethodology = {
  title: 'Jak upozorňujeme na možné přílepkové riziko',
  body: [
    'Přílepkové riziko se zobrazuje jen u pozměňovacích návrhů, kde se potkají dostupné signály z textu návrhu a samostatné AI stanovisko. Heuristika sleduje, jestli amendment novelizuje jiné zákony než hlavní tisk a zda přidává novou citlivou oblast, například justici, média, volby nebo bezpečnostní pravomoci.',
    'AI zároveň posuzuje, zda je pozměňovací návrh věcně související s hlavním tiskem. Štítek „Přílepek“ nebo „Možný přílepek“ proto není právní závěr ani obvinění. Je to upozornění, že text stojí za ruční kontrolu a že v detailu je dobré číst heuristiku i AI vysvětlení společně.',
  ],
  note: 'Pokud se AI a heuristika neshodují, dashboard to neskrývá: detail vždy ukazuje AI stanovisko, míru jistoty i technické signály zvlášť.',
};

export interface TabsGuideItem {
  tab: 'today' | 'tracked' | 'analyses' | 'methodology';
  label: string;
  question: string;
  description: string;
  accentColor: string;
}

export const tabsGuide: TabsGuideItem[] = [
  { tab: 'today', label: 'Dnes', question: 'Denní přehled', description: 'Nové návrhy, posuny ve zpracování a dokončené verdikty.', accentColor: '#f472b6' },
  { tab: 'tracked', label: 'Sledované návrhy', question: 'Aktivní tisky', description: 'Karty všech aktivních tisků: riziko nebo priorita, fáze, 1. čtení, výbor a známý další krok.', accentColor: '#60a5fa' },
  { tab: 'analyses', label: 'Analýzy', question: 'Hotové verdikty', description: 'AI závěry se shrnutím, spolehlivostí a stejnou legislativní cestou jako u sledovaných návrhů.', accentColor: '#34d399' },
  { tab: 'methodology', label: 'Jak to funguje', question: 'Metodika a limity', description: 'Jak číst karty, výbory, termíny, odznaky, AI limity a FAQ.', accentColor: '#e5e7eb' },
];

export const tabsExpertDetail = `Filtrace mátoucích položek probíhá na publish straně (publish-proposal-lifecycle.js), nikoli v UI. Záložka „Sledované návrhy" defaultně skrývá tracking_level=archival a položky s lifecycle_state in (new, tracked) starší 14 dní bez eventu. Záložka „Analýzy" zobrazuje výhradně položky s presentation_state=ready (po reconcile s analysis_complete=true), placeholder/error/no_pdf jdou do diagnostics_only[] a v UI se objeví jen ve sbalené Diagnostice na této stránce.`;

// ─── Lifecycle state definitions ──────────────────────────────────────────────

export interface LifecycleStateDefinition {
  // Stejné hodnoty jako presentation_state v lifecycleFormat.ts —
  // odvozují CSS class .presentation-state--{state} pro vizuální shodu
  // s odznaky na záložce „Sledované návrhy".
  state: 'tracked_only' | 'in_analysis' | 'analysis_synced' | 'archival';
  label: string;
  description: string;
}

export const lifecycleStates: LifecycleStateDefinition[] = [
  { state: 'tracked_only',    label: 'Sleduje se',    description: 'Návrh je v evidenci, ale zatím neproběhla AI analýza.' },
  { state: 'in_analysis',     label: 'V AI analýze',  description: 'AI právě běží nebo čeká ve frontě na zpracování.' },
  { state: 'analysis_synced', label: 'Hotová analýza', description: 'AI vydala verdikt, najdete ho v záložce Analýzy.' },
  { state: 'archival',        label: 'Archiv',         description: 'Technický nebo rutinní návrh. Systém ho sleduje, ale do AI analýzy nejde.' },
];

export const badgesExpertDetail = `Riziko je odvozené z tracking_score (0–100): 0–29 archival, 30–49 low, 50–69 medium, 70–84 high, 85–100 critical. Zdrojem jsou výsledky Stage 2/3 watchdog queue, mapping je deterministický a popsaný v PROPOSAL_LIFECYCLE_TRACKING_CONTRACT.md sekce 4.2. Lifecycle stav je orthogonální (proces) — viz proposal-tracking-state.schema.json enum lifecycle_state: new, tracked, change_detected, enqueued_for_analysis, analysis_synced, published, error.`;

// ─── Lifecycle events ────────────────────────────────────────────────────────
// Vysvětlivky pro odznaky „Událost: ..." na kartě a v detailu návrhu.
// Hodnoty `type` odpovídají EVENT_TYPE_HUMAN v utils/lifecycleFormat.ts —
// label se generuje přes humanizeEventType(type), aby texty zůstaly v sync.

export interface LifecycleEventDefinition {
  type: string;
  description: string;
}

export const lifecycleEvents: LifecycleEventDefinition[] = [
  { type: 'stage_changed',          description: 'Návrh se posunul do další fáze (např. vláda → 1. čtení nebo výbor → 2. čtení).' },
  { type: 'text_hash_changed',      description: 'Text návrhu se změnil. Často to znamená, že byl přepracován nebo upraven.' },
  { type: 'amendment_added',        description: 'Někdo k návrhu podal pozměňovací návrh. Může změnit smysl celého tisku.' },
  { type: 'first_reading_done',     description: 'Návrh prošel prvním čtením v Poslanecké sněmovně.' },
  { type: 'second_reading_done',    description: 'Návrh prošel druhým čtením (po projednání ve výboru).' },
  { type: 'third_reading_done',     description: 'Návrh prošel třetím čtením a putuje k hlasování.' },
  { type: 'analysis_result',        description: 'AI vydala verdikt — najdete ho v záložce Analýzy.' },
  { type: 'approved',               description: 'Návrh byl schválen.' },
  { type: 'rejected',               description: 'Návrh byl zamítnut.' },
  { type: 'president_signed',       description: 'Prezident návrh podepsal.' },
  { type: 'effective',              description: 'Zákon nabyl účinnosti.' },
];

// ─── Legislative path (legislative_status) ────────────────────────────────────
// Vysvětlivky pro blok „Legislativní cesta" na kartě i v detailu (sledované
// návrhy i analýzy). Role a jistota termínu odpovídají controlled vocabulary
// v utils/lifecycleFormat.ts (committeeRoleLabel, legislativeConfidenceLabel),
// aby texty zůstaly v sync s odznaky v UI.

export interface CommitteeRoleDefinition {
  role: 'garancni' | 'vecny' | 'organizacni';
  label: string;
  description: string;
}

export interface DateConfidenceDefinition {
  confidence: 'confirmed' | 'expected' | 'unknown';
  label: string;
  description: string;
}

export const legislativePathIntro = {
  title: 'Legislativní cesta návrhu',
  body: [
    'U sledovaných návrhů i u hotových analýz ukazujeme legislativní cestu: do jaké fáze návrh postoupil, jestli proběhlo 1. čtení, ve kterých výborech je a který je garanční, a jaký je známý další krok.',
    'Na kartě je to zhuštěné do několika prvků: levý pruh rizika nebo priority, odznak fáze, stav před/po 1. čtení, výborový chip, modrý postupový proužek a řádek „Další krok" nebo „Další jednání". V detailu se stejné informace rozpadnou do panelů „Legislativní cesta", „Známý další krok", „Přidělené výbory" a „Plánované události".',
    'Název fáze bereme z řízeného slovníku (například „1. čtení", „Ve výborech" nebo „3. čtení"), ne ze syrového titulku ze stránky PSP. Postup po cestě značíme modře; zelená zůstává vyhrazená výhradně pro nízké riziko.',
  ],
  note: 'Zobrazujeme jen to, co je ve strukturovaných datech PSP. Žádný termín ani výbor si systém nedomýšlí — když datum není ze zdroje, řekneme „termín zatím neznámý" nebo „NEZNÁMO"; když výbor ještě není publikovaný, karta v Analýzách řekne „Výbor zatím nepublikován".',
};

export const committeeRoles: CommitteeRoleDefinition[] = [
  {
    role: 'garancni',
    label: 'Garanční výbor',
    description:
      'Hlavní výbor odpovědný za tisk. Připravuje pro sněmovnu souhrnné stanovisko a pozměňovací návrhy. Na kartě ho ukazujeme jen názvem výboru, bez kódu.',
  },
  {
    role: 'vecny',
    label: 'Věcný výbor',
    description: 'Další výbor, kterému byl tisk přikázán k projednání vedle garančního.',
  },
  {
    role: 'organizacni',
    label: 'Organizační výbor',
    description:
      'Technický krok před 1. čtením — organizační výbor přikazuje tisk výborům. Není to fáze projednávání ani věcné stanovisko, proto ho neukazujeme jako fázi návrhu.',
  },
];

export const dateConfidenceLevels: DateConfidenceDefinition[] = [
  {
    confidence: 'confirmed',
    label: 'Potvrzeno',
    description:
      'Datum pochází ze strukturovaného zdroje PSP (pozvánka na výbor nebo začátek schůze) a leží v budoucnu.',
  },
  {
    confidence: 'expected',
    label: 'Očekáváno',
    description:
      'Krok plyne z pořadí fází, ale konkrétní datum zatím známé není. Ukazujeme „termín zatím neznámý", nedomýšlíme ho.',
  },
  {
    confidence: 'unknown',
    label: 'Neznámo',
    description: 'Pro daný krok nejsou v dostupných datech žádné informace.',
  },
];

// ─── AI does / doesn't ────────────────────────────────────────────────────────

export const aiDoes = [
  'Hledá citlivé pasáže: volby, soudy, práva, kontrola moci.',
  'Vysvětluje, proč může být návrh rizikový.',
  'Sleduje změny textu a pozměňovací návrhy.',
  'Ve finálním kole čte návrh znovu od začátku — nezávisle na předchozích kolech. Pak je kriticky porovná a může jejich závěry odmítnout.',
  'Skládá scénáře, jak by zákon mohl být použit nebo zneužit.',
  'Rozlišuje závažnost a urgenci.',
];

export const aiDoesnt = [
  'Nedává právní stanovisko ani závazný výklad.',
  'Neříká, že návrh je „špatný zákon“.',
  'Nehodnotí politické záměry předkladatele.',
  'Nenahrazuje odbornou debatu nebo konzultaci.',
  'Negarantuje, že nic nepřehlédla.',
];

export const aiExpertDetail = `Tři kola jsou implementovaná v scripts/run-watchdog-queue.js. Stage 1: lokální Qwen 3.5:9b přes Ollama (askOllama + askOllamaWithRetry). Při selhání 3 retry → fallback na OpenAI gpt-4.1-mini (askOpenAIChatFallback). Stage 2: gpt-5-mini se structured output (askOpenAIStage2). Stage 3: gpt-5 s reasoning_effort=high, žádný fallback. Žádné kolo nemá přístup k internetu nebo externím databázím — pracuje jen s textem návrhu a metadaty z PSP RSS. Roční provozní náklad pod $5 (viz PROPOSAL_LIFECYCLE_PLAN_V2_HYBRID_C.md sekce 7).`;

// ─── FAQ ──────────────────────────────────────────────────────────────────────

export const faqItems: FaqItem[] = [
  {
    id: 'high-risk-bad',
    question: 'Znamená vysoké riziko, že je návrh špatný?',
    answer: [
      'Ne. Vysoké riziko znamená, že systém našel signály, které stojí za lidskou kontrolu. Nejde o právní stanovisko ani politický soud.',
    ],
    category: 'basics',
    priority: 'top6',
  },
  {
    id: 'how-risk-detected',
    question: 'Jak systém pozná, že je návrh rizikový?',
    answer: [
      'Hledá kombinaci citlivé oblasti, konkrétní změny textu, možného dopadu a scénáře použití nebo zneužití. Silnější riziko vzniká tehdy, když se tyto signály potkají.',
    ],
    category: 'process',
    priority: 'top6',
  },
  {
    id: 'what-tracks',
    question: 'Co Legislativní monitor sleduje?',
    answer: [
      'Zachycené legislativní návrhy, jejich legislativní cestu a publikované výsledky AI posouzení. Nejde o ruční seznam: karty vznikají ze zveřejněných dat a ukazují jen to, co se podařilo strukturovaně ověřit.',
      'U návrhů proto můžete vidět fázi projednávání, stav před nebo po 1. čtení, přidělený výbor, známé budoucí jednání a případně AI shrnutí. Pokud některá informace chybí, dashboard ji označí jako neznámou místo toho, aby ji odhadoval.',
    ],
    category: 'basics',
    priority: 'top6',
  },
  {
    id: 'why-not-ai',
    question: 'Proč některé návrhy nejdou do AI analýzy?',
    answer: [
      'Protože jsou technické, rutinní nebo mimo hlavní rizikové oblasti. Systém je může dál evidovat, ale neposílá je do AI jen kvůli tomu, že se nově objevily.',
      'Do AI přednostně postupují návrhy z kritických oblastí: volby a referendum, ústavní pořádek, politické strany, justice a trestní proces, policie, média, svoboda projevu a základní práva. Ostatní návrhy se mohou analyzovat později, pokud se změní hlavní text nebo přijde významná legislativní událost.',
    ],
    category: 'process',
    priority: 'top6',
  },
  {
    id: 'when-reanalyze',
    question: 'Kdy systém analyzuje návrh znovu?',
    answer: [
      'Ne při každé změně. Systém má dvouvrstvý filtr — důležitost události a zařazení návrhu (tracking level). Drobné události u rutinních návrhů se přeskočí, aby AI nedělala zbytečnou práci.',
      'Nový návrh v kritické oblasti může jít do AI už před prvním čtením. U ostatních návrhů se znovu reaguje hlavně na změnu hlavního textu, třetí čtení, návrat ze Senátu, schválení, podpis prezidenta, účinnost nebo jiné významné události.',
      'Cílem je zachytit změny, které opravdu mohou změnit hodnocení rizika — ne každý technický update.',
    ],
    category: 'process',
    priority: 'top6',
  },
  {
    id: 'ai-mistakes',
    question: 'Může AI něco přehlédnout?',
    answer: [
      'Ano. Systém pomáhá filtrovat a vysvětlovat rizikové signály, ale negarantuje úplnost. Výstupy je nutné chápat jako podporu lidské kontroly.',
    ],
    category: 'limits',
    priority: 'top6',
  },
{
    id: 'risk-score',
    question: 'Co znamená rizikové skóre?',
    answer: [
      'Je to pořadník pozornosti. Vyšší skóre říká: tady jsou silnější důvody, proč návrh číst dřív a pečlivěji.',
    ],
    category: 'expert',
    priority: 'extended',
  },
  {
    id: 'sensitive-areas',
    question: 'Jaké oblasti jsou citlivé?',
    answer: [
      'Kritické oblasti pro rychlé zařazení do AI jsou volby a referendum, ústavní pořádek a Listina základních práv, politické strany a hnutí, justice a trestní proces, policie a odposlechy, média, rozhlas, televize, svoboda projevu a informace, soukromí, shromažďování, sdružování, diskriminace a menšiny.',
      'Návrh z takové oblasti může jít do AI už z prvního zveřejněného textu. Běžné sektorové novely, například čistě technické nebo rutinní úpravy, se do AI neposílají automaticky.',
    ],
    category: 'process',
    priority: 'extended',
  },
  {
    id: 'title-or-text',
    question: 'Pracuje systém jen s názvem návrhu, nebo i s textem?',
    answer: [
      'Podle dostupnosti dat. Základní přehled může stát na metadatech a shrnutí. Hlubší AI analýza má smysl hlavně tehdy, když je dostupný text návrhu.',
    ],
    category: 'expert',
    priority: 'extended',
  },
  {
    id: 'why-this-proposal',
    question: 'Jak to, že AI ten návrh vůbec vybrala?',
    answer: [
      'Každá analýza má v detailu sekci „Proč se sem tento návrh dostal“: najdete tam, jaký event ji spustil, které pravidlo ji k analýze poslalo a kdy.',
    ],
    category: 'process',
    priority: 'extended',
  },
  {
    id: 'committees-and-dates',
    question: 'Proč někde vidím výbor a datum, a jinde ne?',
    answer: [
      'Výbor se na kartě zobrazí až tehdy, když je přidělení publikované ve strukturovaném feedu. U sledovaných návrhů se jako chip ukazuje hlavně garanční výbor; v Analýzách se při chybějících datech objeví „Výbor zatím nepublikován".',
      'Datum dalšího jednání ukazujeme jen tehdy, když je známé z veřejného zdroje PSP a je v budoucnu. Pokud systém zná jen další krok, ale ne termín, karta řekne „termín zatím neznámý"; pokud nezná ani krok, zobrazí „NEZNÁMO".',
    ],
    category: 'process',
    priority: 'extended',
  },
  {
    id: 'not-equally-detailed',
    question: 'Proč nejsou všechny návrhy analyzované stejně podrobně?',
    answer: [
      'Protože nemají stejnou dostupnost textu, stejnou citlivost ani stejný stav zpracování. Některé skončí po rychlém roztřídění, jiné pokračují do dalších kol AI analýzy.',
    ],
    category: 'process',
    priority: 'extended',
  },
  {
    id: 'not-complete',
    question: 'Co znamená, že analýza není dokončená?',
    answer: [
      'Chybí některý krok, finální rozhodnutí, dostupný text nebo běh skončil chybou. Dashboard to má ukázat jako stav zpracování, ne jako hotový výsledek.',
    ],
    category: 'expert',
    priority: 'extended',
  },
  {
    id: 'can-change',
    question: 'Může se výsledek změnit?',
    answer: [
      'Ano. Změnit ho může nový text, oprava dat, další běh analýzy nebo změna návrhu v průběhu projednávání.',
    ],
    category: 'process',
    priority: 'extended',
  },
  {
    id: 'analysis-status',
    question: 'Jak poznám, že je analýza dokončená?',
    answer: [
      'U každé položky je viditelný stav zpracování. Jen analýza označená jako „Hotová analýza" je závěr — rozpracovaná, chybová nebo neposouzená položka závěr není a neměla by se tak číst.',
    ],
    category: 'expert',
    priority: 'extended',
  },
  {
    id: 'not-risky',
    question: 'Proč některý návrh není označen jako rizikový?',
    answer: [
      'V dostupných datech se nenašel dost silný signál, nebo návrh zatím neprošel hlubší analýzou.',
    ],
    category: 'limits',
    priority: 'extended',
  },
  {
    id: 'legally-binding',
    question: 'Je výstup právně závazný?',
    answer: [
      'Ne. Je to pomůcka pro orientaci a kontrolu, ne právní stanovisko.',
    ],
    category: 'limits',
    priority: 'extended',
  },
];
